import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { ROUTE_REDESIGN } from 'js/constants/pathnames';
import * as PATHS from 'new/constants/pathnames';
import { capsuleFromDto } from 'new/modules/settings/capsules/domain';
import { useCanManageCapsules } from 'new/modules/settings/capsules/hooks/useCanManageCapsules';
import { getMutationErrorMessage } from 'new/modules/settings/capsules/utils/getMutationErrorMessage';
import { useCapsulesAdapter, useCapsulesRepository } from 'new/state/capsules';
import type { CapsuleDto } from 'new/stores/capsules/capsules.endpoints';

function buildDetailCopy(dto: CapsuleDto) {
  const capsuleSummary = capsuleFromDto(dto);
  return {
    title: capsuleSummary.name,
    description: capsuleSummary.description,
  };
}

export function useCapsuleDetail() {
  const { capsuleId } = useParams<{ capsuleId: string }>();
  const history = useHistory();
  const { canManage: canManageCapsules, isSessionLoaded } = useCanManageCapsules();
  const {
    fetchCapsule,
    capsuleData: data,
    isCapsuleUninitialized: isUninitialized,
    isLoading,
    isFetching,
    isError,
    error,
  } = useCapsulesRepository();
  const { deleteCapsule, isDeleting } = useCapsulesAdapter();

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (deleteOpen) setActionError(null);
  }, [deleteOpen]);

  useEffect(() => {
    if (!canManageCapsules) return;
    if (!capsuleId) return;
    void fetchCapsule(capsuleId);
  }, [canManageCapsules, capsuleId, fetchCapsule]);

  const backHref = `${ROUTE_REDESIGN}${PATHS.ROUTE_SETTINGS_CAPSULES}`;

  useEffect(() => {
    if (!isSessionLoaded) return;
    if (canManageCapsules) return;
    history.replace(backHref);
  }, [backHref, canManageCapsules, isSessionLoaded, history]);

  const dto = data;

  const detail = useMemo(() => (dto != null ? buildDetailCopy(dto) : null), [dto]);

  const showLoading =
    Boolean(capsuleId) && (isUninitialized || isLoading || isFetching) && dto == null;

  const showError = Boolean(capsuleId) && !showLoading && (isError || !dto || detail == null);

  const fetchErrorMessage = getMutationErrorMessage(error, 'Could not load this capsule.');

  const handleDelete = useCallback(async () => {
    if (!canManageCapsules) return;
    if (!capsuleId) return;
    setActionError(null);
    try {
      await deleteCapsule(capsuleId).unwrap();
      setDeleteOpen(false);
      history.push(backHref);
    } catch (mutationError) {
      const message = getMutationErrorMessage(mutationError, 'Could not delete this capsule.');
      setActionError(message);
    }
  }, [backHref, canManageCapsules, capsuleId, deleteCapsule, history]);

  const clearActionError = useCallback(() => {
    setActionError(null);
  }, []);

  return {
    backHref,
    capsuleId,
    dto,
    detail,
    showLoading,
    showError,
    fetchErrorMessage,
    deleteOpen,
    setDeleteOpen,
    actionError,
    clearActionError,
    handleDelete,
    isDeleting,
  };
}
